import type { Cart, MarketingItem, SessionInfo, StoreWithOpeningHours } from '@altazion/commerce-sdk-core'
import type Handlebars from 'handlebars'

export interface AltazionDeclarativeViewModel {
  cart: Cart | null
  session: SessionInfo | null
  store: StoreWithOpeningHours | null
  stores: StoreWithOpeningHours[]
  marketingItem: MarketingItem | null
  marketingItems: MarketingItem[]
  slots: Record<string, unknown>
}

export interface AltazionDeclarativeRendererHandle {
  update<K extends keyof AltazionDeclarativeViewModel>(
    key: K,
    value: AltazionDeclarativeViewModel[K]
  ): void
  updateSlot(name: string, value: unknown): void
  render(): void
  dispose(): void
}

type HandlebarsRuntime = typeof Handlebars
type ViewModelKey = keyof AltazionDeclarativeViewModel
type DataSourceReference =
  | { kind: 'state', key: ViewModelKey }
  | { kind: 'slot', key: string }

const DATA_SOURCE_ATTRIBUTE = 'hx-altazion-data-source'
const DATA_STATUS_ATTRIBUTE = 'hx-altazion-data-status'
const RENDER_TEMPLATE_ATTRIBUTE = 'hx-render-template'

const DATA_SOURCE_ALIASES: Record<string, ViewModelKey> = {
  cart: 'cart',
  session: 'session',
  store: 'store',
  stores: 'stores',
  'marketing-item': 'marketingItem',
  'marketing-items': 'marketingItems',
}

export function createAltazionDeclarativeRenderer(
  handlebars?: HandlebarsRuntime
): AltazionDeclarativeRendererHandle {
  const state: AltazionDeclarativeViewModel = {
    cart: null,
    session: null,
    store: null,
    stores: [],
    marketingItem: null,
    marketingItems: [],
    slots: {},
  }

  const templateCache = new Map<string, (context: AltazionDeclarativeViewModel) => string>()

  const render = (): void => {
    if (typeof document === 'undefined') {
      return
    }

    for (const element of document.querySelectorAll(`[${DATA_SOURCE_ATTRIBUTE}]`)) {
      const rawSource = element.getAttribute(DATA_SOURCE_ATTRIBUTE)
      const source = resolveDataSource(rawSource)
      if (!source) {
        continue
      }

      const rawStatus = element.getAttribute(DATA_STATUS_ATTRIBUTE)
      const sourceValue = resolveSourceValue(state, source)
      const isVisible = matchesStatus(rawStatus, sourceValue)
      applyVisibility(element, isVisible)
      renderElementTemplate(element, isVisible, state, sourceValue, handlebars, templateCache)
    }
  }

  return {
    update(key, value): void {
      state[key] = value
      render()
    },
    updateSlot(name, value): void {
      state.slots[name] = value
      render()
    },
    render,
    dispose(): void {
      templateCache.clear()
    },
  }
}

function matchesStatus(status: string | null, value: unknown): boolean {
  if (Array.isArray(value)) {
    switch (status) {
      case 'empty':
        return value.length === 0
      case 'not-empty':
        return value.length > 0
      default:
        return true
    }
  }

  switch (status) {
    case 'null':
      return value == null
    case 'not-null':
      return value != null
    default:
      return true
  }
}

function resolveDataSource(source: string | null): DataSourceReference | null {
  if (!source) {
    return null
  }

  if (source.startsWith('slot:')) {
    const slotKey = source.slice(5).trim()
    return slotKey.length > 0 ? { kind: 'slot', key: slotKey } : null
  }

  const resolvedKey = DATA_SOURCE_ALIASES[source]
  return resolvedKey ? { kind: 'state', key: resolvedKey } : null
}

function resolveSourceValue(
  state: AltazionDeclarativeViewModel,
  source: DataSourceReference
): unknown {
  if (source.kind === 'slot') {
    return state.slots[source.key] ?? null
  }

  return state[source.key]
}

function applyVisibility(element: Element, isVisible: boolean): void {
  element.setAttribute('aria-hidden', isVisible ? 'false' : 'true')

  if (element instanceof HTMLElement) {
    element.hidden = !isVisible
  } else if (isVisible) {
    element.removeAttribute('hidden')
  } else {
    element.setAttribute('hidden', '')
  }
}

function renderElementTemplate(
  element: Element,
  isVisible: boolean,
  state: AltazionDeclarativeViewModel,
  sourceValue: unknown,
  handlebars: HandlebarsRuntime | undefined,
  templateCache: Map<string, (context: AltazionDeclarativeViewModel) => string>
): void {
  if (!isVisible || !element.hasAttribute(RENDER_TEMPLATE_ATTRIBUTE) || !handlebars) {
    return
  }

  const templateId = element.getAttribute(RENDER_TEMPLATE_ATTRIBUTE)
  if (!templateId) {
    return
  }

  const template = resolveTemplate(templateId, handlebars, templateCache)
  if (!template) {
    return
  }

  element.innerHTML = template({
    ...state,
    slot: sourceValue,
  } as AltazionDeclarativeViewModel)
}

function resolveTemplate(
  templateId: string,
  handlebars: HandlebarsRuntime,
  templateCache: Map<string, (context: AltazionDeclarativeViewModel) => string>
): ((context: AltazionDeclarativeViewModel) => string) | null {
  const cached = templateCache.get(templateId)
  if (cached) {
    return cached
  }

  if (typeof document === 'undefined') {
    return null
  }

  const templateElement = document.getElementById(templateId)
  if (!templateElement) {
    console.warn(`[AltazionHtmx] Template not found: ${templateId}`)
    return null
  }

  const compiled = handlebars.compile(templateElement.innerHTML)
  templateCache.set(templateId, compiled)
  return compiled
}